import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export const MAX_ZOOM = 3

interface ZoomAnchor {
  scrollTop: number
  scrollLeft: number
  clientWidth: number
  clientHeight: number
  oldZoom: number
}

export function useContinuousZoom(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  pageDimensions: { w: number; h: number } | null,
  containerSize: { w: number; h: number },
  scrollPadding: number
) {
  const [zoom, setZoom] = useState(0)
  const [minZoom, setMinZoom] = useState(0)
  const zoomAnchorRef = useRef<ZoomAnchor | null>(null)
  const effectiveZoomRef = useRef(0)

  const calculateMinZoom = useCallback(() => {
    if (!pageDimensions || containerSize.w === 0 || containerSize.h === 0) {
      return 0
    }
    const padding = scrollPadding * 2
    const scaleX = (containerSize.w - padding) / pageDimensions.w
    const scaleY = (containerSize.h - padding) / pageDimensions.h
    return Math.min(scaleX, scaleY)
  }, [pageDimensions, containerSize, scrollPadding])

  useEffect(() => {
    const newMin = calculateMinZoom()
    if (newMin > 0) {
      setMinZoom(newMin)
      setZoom((prev) => (prev === 0 ? newMin : Math.max(prev, newMin)))
    }
  }, [calculateMinZoom])

  const effectiveZoom = useMemo(() => Math.max(zoom, minZoom), [zoom, minZoom])
  effectiveZoomRef.current = effectiveZoom

  const captureAnchor = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || effectiveZoomRef.current === 0) return
    zoomAnchorRef.current = {
      scrollTop: container.scrollTop,
      scrollLeft: container.scrollLeft,
      clientWidth: container.clientWidth,
      clientHeight: container.clientHeight,
      oldZoom: effectiveZoomRef.current,
    }
  }, [scrollContainerRef])

  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current
    const container = scrollContainerRef.current
    if (!anchor || !container || effectiveZoom === 0) return

    const ratio = effectiveZoom / anchor.oldZoom
    const oldCenterY = anchor.scrollTop + anchor.clientHeight / 2
    const oldCenterX = anchor.scrollLeft + anchor.clientWidth / 2

    const newCenterY = (oldCenterY - scrollPadding) * ratio + scrollPadding
    const newCenterX = (oldCenterX - scrollPadding) * ratio + scrollPadding

    container.scrollTop = Math.max(0, newCenterY - container.clientHeight / 2)
    container.scrollLeft = Math.max(0, newCenterX - container.clientWidth / 2)

    zoomAnchorRef.current = null
  }, [effectiveZoom, scrollContainerRef, scrollPadding])

  const zoomIn = useCallback(() => {
    captureAnchor()
    setZoom((prev) => Math.min(prev * 1.5, MAX_ZOOM))
  }, [captureAnchor])

  const zoomOut = useCallback(() => {
    captureAnchor()
    setZoom((prev) => Math.max(prev / 1.5, minZoom))
  }, [minZoom, captureAnchor])

  const resetZoom = useCallback(() => {
    captureAnchor()
    setZoom(minZoom)
  }, [minZoom, captureAnchor])

  // Jump straight to a zoom level without anchoring on the current viewport
  // center — for callers (region framing) that set the scroll position
  // themselves right after.
  const setZoomLevel = useCallback(
    (level: number) => {
      setZoom(Math.min(Math.max(level, minZoom), MAX_ZOOM))
    },
    [minZoom]
  )

  const isAtMinZoom = minZoom > 0 && Math.abs(effectiveZoom - minZoom) < 0.001
  const isAtMaxZoom = effectiveZoom >= MAX_ZOOM - 0.001

  return {
    effectiveZoom,
    minZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoomLevel,
    isAtMinZoom,
    isAtMaxZoom,
  }
}
